import { useEffect, useRef, useState } from 'react';
import { AdaptivePracticeContext } from '../../../context/context';
import { PlayFrame } from '../../adaptivePractice/frame/PlayFrame';

/**
 * A quiz on paper - each question DRAWN exactly as it is played.
 *
 * A teacher creates, views and edits a quiz on the play screen, so the sheet shows the same thing:
 * the question in its frame, without the menus down the sides. Everything a question is made of -
 * the frame, the boxes, where a picture sits - comes from this app's stylesheets, its fonts and
 * the window it is laid out against, so a question is both rendered AND drawn here, and what
 * leaves is a picture.
 *
 * It was built the other way first, and this is why it is not that any more: the question went to
 * teacher-web as HTML with every computed style written onto its elements, and teacher-web drew
 * it there. A stylesheet is not the list of values it computes to - it is also which box contains
 * which, what a percentage is a percentage OF, and where an absolutely positioned part hangs
 * from, and none of that travels with a list of declarations. The questions arrived with the
 * question text sitting on top of the options, the frame squeezed into two thirds of its width,
 * and a heading cut off because the font it had been measured in does not exist in that app.
 * Drawn here there is nothing to reproduce: it is the same DOM the teacher is looking at.
 *
 * A picture is also the one thing Word can place exactly as it looks - it has no flexbox, so a
 * question laid out as elements came out of the .doc collapsed into a column however carefully it
 * was written.
 */

/** Long enough for the browser to lay the questions out before they are read back. */
export const LAYOUT_SETTLE = 120;

/**
 * HOW MANY QUESTIONS ARE IN THE BROWSER AT ONCE.
 *
 * Fifty play screens on one page, a hundred pictures of them - the questions and their answers -
 * and the lot handed over in a single message of sixty-odd megabytes is more than a tab has memory
 * for, and from the outside that looks like a download that simply never arrives.
 *
 * So a quiz is drawn a few questions at a time: this many are rendered, drawn and handed over,
 * then taken down before the next few are rendered at all. What is held at any moment is this
 * number of questions rather than the whole quiz, whatever the quiz costs - and the parent is
 * given each batch as it is finished, so nothing waits on the end.
 */
const BATCH_SIZE = 5;

/**
 * HOW MANY QUESTIONS ONE OPENING OF THIS SCREEN DRAWS before handing over to a fresh one.
 *
 * Freeing each drawing as it goes is not enough on its own: across a quiz of fifty, what the
 * browser keeps of every drawing and every question shown still adds up to more than a tab has,
 * and the tab dies near the end. A screen that is closed gives ALL of it back. So this many are
 * drawn, the parent is told where to carry on, and it opens the screen again from there.
 */
export const QUESTIONS_PER_OPENING = 15;

/**
 * Drawn larger than it is laid out, so it is still sharp on paper, and no larger than that.
 *
 * A question is laid out around 1280 wide and printed about 600 wide, so even at 1 to 1 there is
 * twice the detail the page can show. This is the margin on top of that - about 220 dots to the
 * inch on A4, which is sharp - and it is the single biggest thing the wait is made of: drawing
 * costs area, so 1.5 is more than twice the work of 1. A quiz is drawn twice over, once as
 * questions and once as their answers, which is why this is worth keeping honest.
 */
export const PIXEL_SCALE = 1.25;

/**
 * WHAT A DRAWN QUESTION IS SAVED AS.
 *
 * JPEG, not PNG, and the difference is not subtle: a lossless picture of a whole play screen runs
 * to the better part of a megabyte, so a quiz of fifty made a .doc of forty - which Word then has
 * to open - and every one of those pictures had to be encoded, carried between two apps and
 * written into the document. At this quality a question is a fraction of that size, and a
 * question is a flat frame with large text rather than a photograph, so there is nothing in it
 * for the eye to miss at the size it prints.
 *
 * It needs no transparency: the drawing is taken on white.
 */
export const PICTURE_TYPE = 'image/jpeg';
export const PICTURE_QUALITY = 0.85;

/**
 * Nothing scrolls on paper.
 *
 * A question whose content is taller than the frame scrolls on screen, and a picture of it would
 * show only the part that happens to be in view. So anything that scrolls is opened out first: its
 * height becomes its content's height and what it was hiding is simply there. Opening a child makes
 * its parent overflow in turn, so this runs until nothing is left clipped, deepest box first.
 */
const openOutScrollers = (root: HTMLElement) => {
  const opened: HTMLElement[] = [];
  for (let pass = 0; pass < 4; pass += 1) {
    const clipped = (Array.from(root.querySelectorAll<HTMLElement>('*')) as HTMLElement[])
      .reverse()
      .filter((element) => {
        const overflow = window.getComputedStyle(element).overflowY;
        if (overflow === 'visible') return false;
        // Height only, and a box that clips without scrolling has to be hiding something worth
        // seeing: `overflow:hidden` is also how a rounded corner is kept tidy or a decoration is
        // held in place, and opening those out would let them sprawl across the page.
        //
        // The scroll height is only a cheap first look. What decides is how far down the box
        // something is actually DRAWN: a scroll height also counts empty and invisible boxes, and
        // one of those opened a graph question out to thousands of pixels of nothing.
        const threshold = overflow === 'auto' || overflow === 'scroll' ? 1 : 8;
        if (element.scrollHeight - element.clientHeight <= threshold) return false;
        return lowestInside(element) - element.clientHeight > threshold;
      });
    if (clipped.length === 0) break;
    clipped.forEach((element) => {
      // The box keeps the size it had as a minimum - a frame that was a whole window tall should
      // still look like one - and grows from there to hold everything drawn inside it, and no
      // further: `auto` would take in the empty boxes too.
      const holds = Math.ceil(lowestInside(element));
      element.style.minHeight = `${element.clientHeight}px`;
      element.style.height = `${Math.max(element.clientHeight, holds)}px`;
      element.style.maxHeight = 'none';
      element.style.overflow = 'visible';
      opened.push(element);
    });
  }
  return opened;
};

/** A card should not end flush against the last thing in it. This is the air underneath. */
const PAPER_CUSHION = 16;

const REPLACED = ['IMG', 'svg', 'SVG', 'CANVAS', 'VIDEO', 'INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'IFRAME'];
const isClear = (colour: string) =>
  !colour || colour === 'transparent' || /rgba\(.*,\s*0\)$/.test(colour);
/**
 * Whether an element puts anything on the screen ITSELF: text, a picture, a graph, a field, or a
 * small coloured or bordered box - an option, a tile, the title bar. A background box half the
 * window tall or more is a container - the frame, the question's area - and is sized by what is
 * in it rather than deciding how far down the question goes. Empty boxes, invisible ones and the parts inside a
 * graph's own drawing count for nothing.
 */
const paints = (element: HTMLElement, edges: DOMRect) => {
  // Inside a graph's own drawing: the graph is measured as a whole, by its <svg>.
  if ((element as unknown as SVGElement).ownerSVGElement) return false;
  const style = window.getComputedStyle(element);
  // Hidden, but not "transparent at the moment": the engine fades parts in one after another, and
  // a part caught before its turn is still part of the question.
  if (style.visibility !== 'visible') return false;
  if (REPLACED.includes(element.tagName)) return true;
  const ownText = Array.from(element.childNodes).some(
    (node) => node.nodeType === Node.TEXT_NODE && (node.textContent || '').trim() !== '',
  );
  if (ownText) return true;
  if (edges.height > window.innerHeight / 2) return false;
  return (
    !isClear(style.backgroundColor) ||
    style.backgroundImage !== 'none' ||
    (parseFloat(style.borderBottomWidth) > 0 && !isClear(style.borderBottomColor))
  );
};
/**
 * How far down a box the things drawn inside it reach.
 *
 * Not the scroll height, for two reasons. It leaves out the bottom margin of the child that
 * overflows, so a question whose options are spaced apart would measure short and lose half its
 * last option. And it counts empty and invisible boxes, so a question with a tall empty one would
 * measure thousands of pixels too long. The lowest edge of everything that is actually drawn, its
 * own margin included, is neither.
 */
const lowestInside = (box: HTMLElement) => {
  const top = box.getBoundingClientRect().top;
  let lowest = 0;
  (Array.from(box.querySelectorAll<HTMLElement>('*')) as HTMLElement[]).forEach((child) => {
    const edges = child.getBoundingClientRect();
    if (edges.height === 0 || !paints(child, edges)) return;
    const margin = parseFloat(window.getComputedStyle(child).marginBottom) || 0;
    lowest = Math.max(lowest, edges.bottom - top + margin);
  });
  return lowest;
};
const contentBottom = (box: HTMLElement) => Math.ceil(lowestInside(box)) + PAPER_CUSHION;

/**
 * A drawing's canvas, let go of the moment its picture has been taken.
 *
 * Each one holds megabytes of pixels outside the JavaScript heap, where the garbage collector does
 * not feel them, so a long quiz piles up dozens before any is collected and the tab runs out of
 * memory part way through. A canvas with no size holds nothing.
 */
export const release = (canvas: HTMLCanvasElement) => {
  canvas.width = 0;
  canvas.height = 0;
};

/**
 * What the drawing's copy of the document leaves out.
 *
 * Every question page but this one: the drawing copies the whole document to take one picture,
 * and the other questions of the batch - their pictures decoded all over again in the copy - are
 * nothing but weight to it.
 *
 * And mjx-assistive-mml, MathJax's second copy of every formula, for screen readers, hidden by
 * clipping it to nothing. The drawing does not clip, so it printed each formula twice, one over
 * the other. It has to be left out here rather than hidden by a stylesheet in the copy: the
 * drawing renames tags like it in the copy and writes their styles in, so no rule reaches them.
 */
const onlyThePage = (page: HTMLElement) => (element: Element) =>
  (element.classList.contains('aq-paper') && element !== page) ||
  element.tagName === 'MJX-ASSISTIVE-MML';

/**
 * The parts of a question that are for answering on screen, not for paper: the engine's number
 * keypad and the Clear button of graph and match-the-following questions. The answer box itself
 * stays - a child writes in it. Matched by how their ids START: the engine adds a number to the
 * end of each (KeyBoardContainer_flexColumnContainer_1792…, clearButton_1792…).
 */
export const ON_SCREEN_ONLY = [
  '[id^="KeyBoardContainer_"]',
  '[id^="clearButton"]',
  '[id^="barGraphClearButton"]',
  '[id^="histogramClearButton"]',
].join(', ');

/**
 * HOW A QUESTION IS LAID OUT ON PAPER - the questions the download draws and the sheet shows, and
 * nothing else.
 *
 * As tall as its content. On screen the frame is the window's height, every box inside it
 * 100% of the one around it, and a question that does not fit scrolls or spills. Here each
 * of those boxes is as tall as what is in it, so the browser lays the question out at its
 * own height: nothing to scroll, nothing cut off, no empty space under a short one.
 *
 * From the top. The screen centres a question in its area; with no spare height there is
 * nothing to centre in.
 *
 * Still. The engine brings options in one after another, and a question drawn mid-way would
 * be missing the ones that had not arrived. Paper shows every part where it ends up.
 */
export const PAPER_CSS = `
  /* THE PAPER LAYOUT - on only once the pictures are pinned (growToContent adds aq-paper-grow).
     Until then the question is laid out as the screen lays it out, and that is the size each
     picture is pinned at. Switched on first, the frame stopped being a window tall, the engine's
     pictures - sized as shares of it - lost what they were shares of, and came out at their own
     file's size before anything could hold them. */
  .aq-paper.aq-paper-grow .frame-container,
  .aq-paper.aq-paper-grow #frame,
  .aq-paper.aq-paper-grow #frame > .row,
  .aq-paper.aq-paper-grow #question-content,
  .aq-paper.aq-paper-grow .question-content {
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
  }
  /* Only the frame takes its size from its content. #question-content is a Bootstrap column, and
     a column's WIDTH is its flex value - setting that shrinks it to its content and pushes the
     whole question to the left of the row. */
  .aq-paper.aq-paper-grow #frame {
    flex: 0 0 auto !important;
  }
  .aq-paper.aq-paper-grow #question-content-inner,
  .aq-paper.aq-paper-grow .question-content-inner {
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow: visible !important;
    justify-content: flex-start !important;
    padding-bottom: 16px !important;
  }
  /* Every part of a question at least as tall as what is in it. The engine fixes many of its own
     boxes as a share of the window - the options' row is height: 20vh - and a box's content that
     overflows it counts for nothing in the layout: the options stood over the frame's edge and the
     next question. max-content as a MINIMUM keeps each box's own height where its content fits,
     and lets it grow only where it does not. Not important: a min-height the engine writes on an
     element itself is kept.

     Not pictures: a picture's "content" is its own file at full size, so under this rule a picture
     the question shows 133px tall came out at the 800px it was drawn at - and the box around it
     with it. Pinned to its size on screen, it counts as that size and nothing more. */
  .aq-paper.aq-paper-grow #question-content-inner *:not(img):not(svg):not(canvas):not(video),
  .aq-paper.aq-paper-grow .question-content-inner *:not(img):not(svg):not(canvas):not(video) {
    min-height: max-content;
  }
  /* Paper shows the question, not the controls a child answers WITH - the number keypad, the Clear
     button - and not its worked answer, which goes to the key at the back. Important, because the
     keypad is also d-flex and Bootstrap's display rule is itself important. */
  ${ON_SCREEN_ONLY.split(', ').map((selector) => `.aq-paper ${selector}`).join(',\n  ')},
  .aq-paper #explanationBox,
  .aq-paper #hintBox,
  .aq-paper .info-box {
    display: none !important;
  }
  /* White paper around each question card, rather than the play screen's own background, so the
     questions sit on the page as a document does - the same in the preview and in both downloads. */
  .aq-paper .frame-container {
    background: #ffffff !important;
  }
  .aq-paper *, .aq-paper *::before, .aq-paper *::after {
    animation: none !important;
    transition: none !important;
  }

`;

/**
 * The play screen a question is shown in on paper: no menus, no close, no sound, nothing to answer
 * with. The same options the builder's cards use, so the sheet and the screen agree - except for
 * `showAnswer`, which asks the engine for the worked answer as well.
 *
 * ASKED FOR ONCE, AND BOTH TAKEN FROM IT. The answer is part of the question's own screen, so a
 * second render of the whole quiz to collect the answers would be the same engine drawing the same
 * questions again, twice the wait and twice the page held in memory.
 */
export const PAPER_OPTIONS = {
  readOnly: true,
  isMusicAllowed: false,
  isToggleFullSceren: false,
  isShowExpNext: false,
  isShowSideMenus: false,
  isShowClose: false,
  isShowReload: false,
  showAnswer: true,
};

/** One question in its play frame, as it goes on paper. */
export const PaperQuestion = ({ response }: { response: any }) => (
  <AdaptivePracticeContext.Provider
    value={
      {
        notificationMessage: '',
        coins: undefined,
        streaks: undefined,
        isSafeZone: false,
        isLastQuestion: false,
        meter: { percentage: 0, level: '' },
        isLoader: false,
        isCoinAnimation: false,
        isShowStartPopup: false,
        isShowAdaptivePopup: false,
        isCloseFromClick: false,
        engineObj: response,
        options: PAPER_OPTIONS,
        loadContent: () => {},
        saveQuestionResponse: async () => {},
        getPopUpInfo: () => ({}),
        setShowAdaptivePopup: () => {},
        onclose: () => {},
      } as any
    }
  >
    <PlayFrame />
  </AdaptivePracticeContext.Provider>
);

/**
 * PUT A QUESTION ON PAPER - once its pictures are held at their size.
 *
 * Called once the questions are laid out as the screen lays them out, with their pictures loaded.
 * Each picture - image, drawing, canvas or video - is pinned to the size it is showing, in pixels,
 * and only then is PAPER_CSS's paper layout switched on (the aq-paper-grow class): the frame as
 * tall as its content, and every box grown to fit what is in it. A box can then grow for options
 * that do not fit it, but a picture stays exactly the size the screen gave it. The download and
 * the preview both call this, so both lay a question out the same way.
 */
export const growToContent = (root: HTMLElement) => {
  // Every kind of picture a question draws: an <img>, a drawing in its own <svg> (the outermost
  // one only - the shapes inside it are sized by it), a canvas, a video.
  root
    .querySelectorAll<HTMLElement | SVGElement>(
      '.aq-paper img, .aq-paper svg, .aq-paper canvas, .aq-paper video',
    )
    .forEach((picture) => {
      if (picture instanceof SVGElement && picture.ownerSVGElement) return;
      const box = picture.getBoundingClientRect();
      if (box.width < 1 || box.height < 1) return;
      picture.style.setProperty('width', `${box.width}px`, 'important');
      picture.style.setProperty('height', `${box.height}px`, 'important');
    });
  root.querySelectorAll<HTMLElement>('.aq-paper').forEach((paper) => {
    paper.classList.add('aq-paper-grow');
  });
};

/**
 * Every picture in the questions, loaded.
 *
 * A question is measured to decide how far it has to be opened out, and a picture that has not
 * arrived yet measures as nothing - so the question was measured short, nothing was opened, and
 * the drawing cut off whatever did not fit. It is the same quiz that came out right one press and
 * wrong the next: it was a race, and the browser won it at different points each time.
 */
const LOADING_GIVE_UP = 10000;
export const waitForPictures = async (root: HTMLElement) => {
  const pending = Array.from(root.querySelectorAll('img'))
    .filter((image) => !image.complete)
    .map(
      (image) =>
        new Promise<void>((resolve) => {
          image.addEventListener('load', () => resolve(), { once: true });
          image.addEventListener('error', () => resolve(), { once: true });
        }),
    );
  if (pending.length === 0) return;
  // A picture that never arrives must not take the download with it.
  await Promise.race([
    Promise.all(pending),
    new Promise<void>((resolve) => { setTimeout(resolve, LOADING_GIVE_UP); }),
  ]);
};

/**
 * EVERY PICTURE ON THIS PAGE, DECODED, BEFORE IT IS DRAWN.
 *
 * Loaded is not the same as ready to paint. An image can report itself complete while the browser
 * is still decoding it, and the drawing copies the page into a document of its own at that moment
 * - so a question could go into the sheet with its pictures missing while the very same question
 * showed them on screen. `decode()` is the browser's own promise that the picture can be painted
 * now; it is asked of each image on the page about to be drawn, and of nothing else.
 *
 * A picture that never decodes - a broken address, a file that is not an image - must not hold
 * the download up, so each is given a moment and then let go.
 */
const DECODE_GIVE_UP = 4000;
export const readyToPaint = async (page: HTMLElement) => {
  const images = Array.from(page.querySelectorAll('img'));
  await Promise.all(
    images.map((image) =>
      Promise.race([
        image.decode().catch(() => undefined),
        new Promise<void>((resolve) => { setTimeout(resolve, DECODE_GIVE_UP); }),
      ]),
    ),
  );
  return images;
};

/**
 * EVERY SVG PICTURE BECOMES A PNG OF ITSELF before the drawing sees it.
 *
 * The objects a question asks a child to count - the birds, the pineapples, the carrots - are SVG
 * files shown through an <img>, and html2canvas leaves them out even when they are loaded,
 * decoded and sized, on the page and in its own copy of it: the box they sit in comes out empty
 * while the same question shows them on screen. Single objects, ovals and shapes come out because
 * they are either not SVG or not pictures at all.
 *
 * So the drawing is never handed one. Each SVG picture is drawn by the browser onto a canvas at
 * exactly the size it is showing, and replaced by that - an ordinary bitmap, which the drawing
 * has never had trouble with. It keeps the picture's own shape, centred in its box, the way an SVG
 * fits itself to the box it is given on screen.
 */
export const flattenSvgPictures = async (page: HTMLElement) => {
  const svgPictures = Array.from(page.querySelectorAll('img')).filter((image) => {
    const src = image.getAttribute('src') || '';
    return src.startsWith('data:image/svg+xml') || /\.svg([?#]|$)/i.test(src);
  });
  let flattened = 0;
  svgPictures.forEach((image) => {
    const box = image.getBoundingClientRect();
    // Not showing, or not loaded: there is nothing to copy, and it is left as it is.
    if (box.width < 1 || box.height < 1 || !image.complete || !image.naturalWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(box.width * PIXEL_SCALE);
    canvas.height = Math.ceil(box.height * PIXEL_SCALE);
    const context = canvas.getContext('2d');
    if (!context) return;
    // The picture's own proportions, fitted inside its box and centred - what an SVG does on
    // screen, rather than stretching to fill.
    const shape = image.naturalWidth / (image.naturalHeight || image.naturalWidth);
    let width = canvas.width;
    let height = width / shape;
    if (height > canvas.height) {
      height = canvas.height;
      width = height * shape;
    }
    try {
      context.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
      // HELD AT THE SIZE IT IS SHOWING. The bitmap is drawn larger than the box for sharpness,
      // and a picture whose height comes from its own file would grow to match it - pushing the
      // options below it out of the question's frame.
      image.style.width = `${box.width}px`;
      image.style.height = `${box.height}px`;
      image.setAttribute('src', canvas.toDataURL('image/png'));
      flattened += 1;
    } catch (error) {
      // Left as an SVG: a picture the drawing may still manage beats one taken away.
      console.log('[adaptive-quiz] could not flatten a picture', error);
    }
    release(canvas);
  });
  // All of them first, then one wait: each src has just changed, and none is ready until decoded.
  await Promise.all(
    svgPictures.map((image) =>
      Promise.race([
        image.decode().catch(() => undefined),
        new Promise<void>((resolve) => { setTimeout(resolve, DECODE_GIVE_UP); }),
      ]),
    ),
  );
  return flattened;
};

/**
 * What a picture that will NOT be drawn looks like at the moment it was drawn - said out loud, so
 * a question that comes out with an empty box tells us why rather than leaving us to guess. Only
 * the pictures that are not ready, or that the layout has given no size, are mentioned.
 */
const reportUnpainted = (images: HTMLImageElement[], questionNumber: number) => {
  images.forEach((image) => {
    const box = image.getBoundingClientRect();
    // A picture with no box at all is one the page hides on purpose - an icon of the screen, the
    // same on every question - and saying so on all fifty of them hides what matters.
    if (box.width < 1 || box.height < 1) return;
    const unready = !image.complete || image.naturalWidth === 0;
    if (!unready) return;
    console.log('[adaptive-quiz] question', questionNumber, 'picture not drawable:', {
      src: (image.getAttribute('src') || '').slice(0, 60),
      complete: image.complete,
      naturalWidth: image.naturalWidth,
      width: Math.round(box.width),
      height: Math.round(box.height),
    });
  });
};

/** How long something took, in the words a person reads a clock in: "1 min 23 s", or "8.4 s". */
const elapsedSince = (start: number) => {
  const seconds = (performance.now() - start) / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  return `${Math.floor(seconds / 60)} min ${Math.round(seconds % 60)} s (${seconds.toFixed(1)} s)`;
};

/** Two frames: one for the browser to lay out what has just loaded, one to settle on it. */
export const settled = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

/** Anything bigger than this is not a picture in a question, and a sheet should not carry it. */
const MAX_EMBEDDED_IMAGE = 2 * 1024 * 1024;

/** One address, read once, however many questions draw it. */
const readAsData = async (absolute: string, cache: { [src: string]: string }) => {
  if (cache[absolute] !== undefined) return cache[absolute];
  try {
    const response = await fetch(absolute);
    if (!response.ok) return '';
    const blob = await response.blob();
    if (blob.size > MAX_EMBEDDED_IMAGE) return '';
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('unreadable image'));
      reader.readAsDataURL(blob);
    });
    cache[absolute] = dataUrl;
    return dataUrl;
  } catch (error) {
    console.log('[adaptive-quiz] could not embed an image', absolute, error);
    return '';
  }
};

/**
 * Every picture is read in before anything is drawn.
 *
 * Drawing works through a canvas, and a picture served from another address taints that canvas:
 * the drawing cannot be read back at all, and the download fails on a question that looked
 * perfectly fine on screen. Fetched here and written into the page as data, every picture belongs
 * to this page by the time it is drawn.
 */
export const embedImages = async (root: HTMLElement, cache: { [src: string]: string }) => {

  // A question draws a picture three ways: an <img>, an <image> inside an svg, and a background.
  const drawn: { element: Element; attribute: string }[] = [
    ...Array.from(root.querySelectorAll('img')).map((element) => ({ element, attribute: 'src' })),
    ...Array.from(root.querySelectorAll('image')).map((element) => ({
      element,
      attribute: element.getAttribute('href') !== null ? 'href' : 'xlink:href',
    })),
  ];

  await Promise.all([
    ...drawn.map(async ({ element, attribute }) => {
      const raw = element.getAttribute(attribute) || '';
      if (!raw || raw.startsWith('data:')) return;
      const absolute = new URL(raw, window.location.href).href;
      const dataUrl = await readAsData(absolute, cache);
      if (dataUrl) element.setAttribute(attribute, dataUrl);
    }),
    ...Array.from(root.querySelectorAll<HTMLElement>('*')).map(async (element) => {
      const background = window.getComputedStyle(element).backgroundImage;
      const address = /url\("?([^")]+)"?\)/.exec(background || '');
      if (!address || address[1].startsWith('data:')) return;
      const dataUrl = await readAsData(new URL(address[1], window.location.href).href, cache);
      if (dataUrl) element.style.backgroundImage = `url("${dataUrl}")`;
    }),
  ]);
};

/**
 * A QUESTION ARRIVES ON SCREEN BY MOVING, AND A PICTURE OF IT MUST BE OF WHERE IT LANDS.
 *
 * The parts of a question slide in - the heading from the left, the options from the top
 * (renderer.css, the slideInFrom… animations) - over the first second, and drawing does not
 * photograph the screen: it copies the page into a document of its own, where every animation
 * starts again from its first frame. So each question was drawn as it was still arriving, the
 * options still travelling over the heading and the heading still 40% left of where it ends up -
 * which is the overlapping text, and the words coming in from the side.
 *
 * Waiting does not fix it, because the copy's animations begin when the copy is made. The copy is
 * told not to animate at all instead, and every one of these animations ends where the element
 * simply is, so what is drawn is exactly where it all comes to rest.
 */
export const stillTheQuestion = async (cloned: Document) => {
  const stillness = cloned.createElement('style');
  stillness.textContent = `*, *::before, *::after {
    animation: none !important;
    transition: none !important;
  }`;
  cloned.head.appendChild(stillness);
  await picturesDecodedIn(cloned);
};

/**
 * THE COPY'S PICTURES, DECODED - which is not the same as the page's.
 *
 * The drawing does not paint the page; it copies it into a document of its own and paints the
 * copy, and every picture in that copy is fetched and decoded all over again. It then draws a
 * picture only if the copy can say how big it is (`naturalWidth`), and asks that the moment the
 * copy has loaded - not once it has decoded. An SVG drawn as a picture is the slow kind to decode,
 * so on a busy page some lost that race and came out as empty boxes: the pineapples, the birds,
 * the carrots, while the ovals beside them - drawn as shapes rather than as pictures - always
 * made it. Waiting for the page's own pictures could not help, because those are not the ones
 * being painted.
 *
 * This runs on the copy, after it is made and before it is painted, which is the one moment that
 * matters. A picture that never decodes is given a moment and let go, so it cannot hold the
 * download up.
 */
const picturesDecodedIn = async (cloned: Document) => {
  const images = Array.from(cloned.querySelectorAll('img'));
  await Promise.all(
    images.map((image) =>
      Promise.race([
        image.decode().catch(() => undefined),
        new Promise<void>((resolve) => { setTimeout(resolve, DECODE_GIVE_UP); }),
      ]),
    ),
  );
  // What the drawing will skip, said out loud: a picture the copy still cannot size.
  images.forEach((image) => {
    if (image.naturalWidth > 0 && image.naturalHeight > 0) return;
    console.log('[adaptive-quiz] a picture the drawing will skip:', {
      src: (image.getAttribute('src') || '').slice(0, 60),
      complete: image.complete,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
    });
  });
};

/**
 * The answer box as a teacher needs it on paper: the worked answer, and nothing that belongs to a
 * screen - no Next button to press, no animated figure beside it, no arrow for folding it away.
 */
export const stillTheAnswer = async (cloned: Document) => {
  await stillTheQuestion(cloned);
  const trimmed = cloned.createElement('style');
  trimmed.textContent = `#nextButton, #downButton, #upButton, .next-question-btn, .info-boy {
    display: none !important;
  }`;
  cloned.head.appendChild(trimmed);
};

/**
 * The fonts have to be here before the question is drawn. Drawing lays the text out again rather
 * than photographing it, so a face that has not arrived yet is laid out in the fallback - wider
 * letters, and words wrapping where they do not on screen.
 */
export const waitForFonts = async () => {
  try {
    await (document as any).fonts?.ready;
  } catch {
    // A question drawn a moment early is still a question; a download that never comes is not.
  }
};

export interface PrintedQuestion {
  /** The question, drawn: a picture, which carries nothing of this app with it. */
  src: string;
  /** What it was drawn at, so the page it goes on can keep its shape. */
  width: number;
  height: number;
  points: number;
}

/** The worked answer to one of them, for the key at the back of the sheet. */
export interface PrintedAnswer {
  /** Which question it answers, counting from 1 - a question with no worked answer has no entry. */
  number: number;
  src: string;
  width: number;
  height: number;
}

/**
 * Renders the quiz's questions off-screen, each in its own play frame, draws each one, and hands
 * back the pictures. It shows nothing a teacher sees: the screen that uses it has its own
 * "preparing" message.
 */
export function QuizPrinter(props: {
  questions: { response?: any; points?: number }[];
  /**
   * A few questions, drawn and ready to go - and whether they are the last of them.
   *
   * Handed over batch by batch rather than all at the end: a long quiz is too much to hold, and
   * what has been drawn is of use to the parent as soon as it exists.
   */
  onBatch: (
    printed: PrintedQuestion[],
    answers: PrintedAnswer[],
    isLast: boolean,
    /** Set when this opening has drawn its share: the question the next opening starts at. */
    nextFrom?: number,
  ) => void;
  /** Where it has got to, so a teacher watching a dialog sees it moving. */
  onProgress?: (done: number, total: number) => void;
  /** The question this opening starts at - not the first, when an earlier opening drew those. */
  startAt?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  /** Where in the quiz this batch starts. Changing it renders the next few and takes down these. */
  const startAt = Math.max(0, props.startAt || 0);
  const [from, setFrom] = useState(startAt);
  const drawnUpTo = useRef(-1);
  /** Addresses read once for the whole quiz, not once per batch. */
  const pictureCache = useRef<{ [src: string]: string }>({});
  /** When the first batch began, so the last one can say how long the whole quiz took. */
  const startedAt = useRef<number | null>(null);


  useEffect(() => {
    if (drawnUpTo.current === from) return;
    const container = containerRef.current;
    if (!container || props.questions.length === 0) return;

    // A moment, so the browser has laid the questions out and the engine's own images have been
    // asked for.
    //
    // A timer, not an animation frame: this screen runs in an iframe belonging to another origin,
    // and a browser throttles animation frames there - off-screen, it stops delivering them
    // altogether, so the download was prepared only in a window nobody was looking at.
    const handle = setTimeout(async () => {
      drawnUpTo.current = from;
      const isLast = from + BATCH_SIZE >= props.questions.length;
      const batchStartedAt = performance.now();
      if (startedAt.current === null) startedAt.current = batchStartedAt;
      try {
        // NOTHING IS MEASURED UNTIL EVERYTHING HAS ARRIVED. A question is measured to decide how
        // far it has to be opened out, and its pictures and its letters are what give it its
        // height - so the fonts and the images come first, and only then is anything read.
        console.log('[adaptive-quiz] waiting for the questions to settle…');
        await waitForFonts();
        await waitForPictures(container);
        await settled();

        growToContent(container);
        await settled();

        // The question's own frame is as tall as its content already (see the stylesheet below);
        // this is for anything INSIDE a question that scrolls - a long table, a list of options.
        console.log('[adaptive-quiz] opening out anything that scrolls…');
        openOutScrollers(container);

        // The pictures are swapped for their own data, which is what lets them be drawn at all -
        // and waited for again, because a src that has just changed has not been decoded yet.
        console.log('[adaptive-quiz] embedding images…');
        await embedImages(container, pictureCache.current);
        await waitForPictures(container);
        await settled();
        console.log('[adaptive-quiz] images embedded');

        // Loaded when a quiz is actually being put on paper, and not before: it is a large
        // library, and every other screen in this app has no use for it.
        const html2canvas = (await import('html2canvas')).default;
        const pages = Array.from(
          container.querySelectorAll<HTMLElement>(':scope > .aq-paper'),
        ) as HTMLElement[];
        const printedQuestions: PrintedQuestion[] = [];
        const printedAnswers: PrintedAnswer[] = [];

        // One at a time, and in order: a drawing holds the whole page in memory while it runs.
        //
        // A question that cannot be drawn is left out rather than taking the download with it - a
        // sheet missing one question is worth more to a teacher than no sheet at all.
        for (let index = 0; index < pages.length; index += 1) {
          // Where this question sits in the QUIZ. The pages on screen are one batch of it, and
          // every number that leaves here - the answer's question number, the progress a teacher
          // reads - has to be about the quiz rather than about the batch.
          const inQuiz = from + index;
          const page = pages[index];
          try {
            // Its pictures decoded and laid out at the size they will be drawn at - and every SVG
            // among them turned into a bitmap, which is what the drawing can be trusted with. This
            // comes BEFORE anything is measured, so the question is measured as it will be drawn.
            // eslint-disable-next-line no-await-in-loop
            const pictures = await readyToPaint(page);
            // eslint-disable-next-line no-await-in-loop
            await settled();
            reportUnpainted(pictures, inQuiz + 1);
            // eslint-disable-next-line no-await-in-loop
            const flattened = await flattenSvgPictures(page);
            if (flattened)
              console.log('[adaptive-quiz] question', inQuiz + 1, ':', flattened, 'SVG pictures made into bitmaps');
            // eslint-disable-next-line no-await-in-loop
            await settled();

            // The page is as tall as its frame, and the frame as its content. The one thing that
            // can still stand past it is a part placed out of the flow - so the page is stretched
            // to hold anything drawn below its edge, or the picture would cut it off.
            const reaches = contentBottom(page);
            if (reaches - page.clientHeight > 1) page.style.height = `${reaches}px`;

            // eslint-disable-next-line no-await-in-loop
            const canvas = await html2canvas(page, {
              scale: PIXEL_SCALE,
              backgroundColor: '#ffffff',
              useCORS: true,
              logging: false,
              ignoreElements: onlyThePage(page),
              onclone: stillTheQuestion,
            });
            printedQuestions.push({
              src: canvas.toDataURL(PICTURE_TYPE, PICTURE_QUALITY),
              width: canvas.width,
              height: canvas.height,
              points: props.questions[inQuiz]?.points ?? 1,
            });
            release(canvas);
            console.log('[adaptive-quiz] drew question', inQuiz + 1);

            // AND NOW THE ANSWER TO IT, from the same screen: brought back into the layout and
            // drawn on its own, so the key at the back of the sheet carries the worked answer
            // without a second copy of the question above it. A question the engine has no worked
            // answer for simply has no entry.
            const answer = page.querySelector<HTMLElement>('#explanationBox, .info-box');
            if (answer) {
              // Paper hides the worked answer in its question (PAPER_CSS), and the engine keeps it
              // inside the keypad's box, which paper hides too. Both are shown for as long as the
              // answer alone is drawn - inline and important, the one thing that outranks the
              // stylesheet - or the answer has no size and its picture is empty.
              const around = (Array.from(page.querySelectorAll<HTMLElement>(ON_SCREEN_ONLY)) as HTMLElement[])
                .filter((control) => control.contains(answer));
              answer.style.setProperty('display', 'block', 'important');
              around.forEach((control) => control.style.setProperty('display', 'flex', 'important'));
              // eslint-disable-next-line no-await-in-loop
              const workedOut = await html2canvas(answer, {
                scale: PIXEL_SCALE,
                backgroundColor: '#ffffff',
                useCORS: true,
                logging: false,
                ignoreElements: onlyThePage(page),
                onclone: stillTheAnswer,
              });
              // A picture with no size is not a picture: the PDF cannot place one, and a blank in
              // the key says nothing a missing entry does not.
              if (workedOut.width > 0 && workedOut.height > 0) {
                printedAnswers.push({
                  number: inQuiz + 1,
                  src: workedOut.toDataURL(PICTURE_TYPE, PICTURE_QUALITY),
                  width: workedOut.width,
                  height: workedOut.height,
                });
              }
              release(workedOut);
              around.forEach((control) => control.style.removeProperty('display'));
              answer.style.removeProperty('display');
              console.log('[adaptive-quiz] drew the answer to question', inQuiz + 1);
            } else {
              console.log('[adaptive-quiz] no worked answer for question', inQuiz + 1);
            }
          } catch (error) {
            console.log('[adaptive-quiz] could not draw question', inQuiz + 1, error);
          }
          props.onProgress?.(inQuiz + 1, props.questions.length);
        }

        console.log('[adaptive-quiz] prepared questions', from + 1, 'to', from + pages.length,
          'in', elapsedSince(batchStartedAt));
        if (isLast && startedAt.current !== null)
          console.log('[adaptive-quiz] TIMER: all', props.questions.length, 'questions drawn in',
            elapsedSince(startedAt.current));
        const next = from + BATCH_SIZE;
        // This opening's share is drawn: the parent opens a fresh screen from the next question.
        const handOver = !isLast && next - startAt >= QUESTIONS_PER_OPENING;
        props.onBatch(printedQuestions, printedAnswers, isLast, handOver ? next : undefined);
        // The next few, which takes these down: the batch on screen is the only part of the quiz
        // being held.
        if (!isLast && !handOver) setFrom(next);
      } catch (error) {
        // Whoever asked for this is waiting on it; an answer they can act on beats a spinner.
        // Said to be the last batch, so the parent stops waiting and works with what it has.
        console.log('[adaptive-quiz] could not prepare the questions for printing', error);
        props.onBatch([], [], true);
      }
    }, LAYOUT_SETTLE);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.questions, from]);

  return (
    <div ref={containerRef} aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0 }}>
      <style>{PAPER_CSS}</style>
      {props.questions.slice(from, from + BATCH_SIZE).map((question, index) => (
        // Keyed by the question's place in the QUIZ, not in the batch: a key that repeats from one
        // batch to the next hands the next question the same page, still carrying the paper layout
        // switched on for the last one - and its pictures were pinned at the paper size, not the
        // screen's. A new key is a new page, laid out as the screen lays it out.
        <div key={from + index} className="aq-paper" style={{ position: 'relative', width: '100vw' }}>
          <PaperQuestion response={question.response} />
        </div>
      ))}
    </div>
  );
}

export default QuizPrinter;
